import { useRef, useState } from "react";

import type { CargaTrabalhoProfissional } from "@/modules/carga-trabalho";
import type { EquipeObraTarget, Profissional } from "@/modules/recursos";
import { VERIFICACAO_ERROR_MESSAGE } from "../definir-equipe-obra.constants";
import { JUSTIFICATIVA_MAX_LENGTH } from "@/shared/components/equipe-obra/equipe-obra.constants";
import { carregarAlocacoesDoPeriodo } from "@/shared/components/equipe-obra/calendario-alocacao-tab/hooks/use-calendario-alocacao";
import { combinarJustificativas, limiteJustificativa } from "./justificativas-salvar";
import {
  detectarConflitos,
  detectarIndisponibilidades,
  papeisObrigatoriosPendentes,
  unirIndisponibilidades,
} from "@/modules/recursos";
import type {
  ConflitoNovaAlocacao,
  IndisponibilidadeNovaAlocacao,
  NovaAlocacao,
  PapelIncompletoItem,
} from "@/modules/recursos";

/** Etapa de verificação em tela, na ordem em que aparecem. */
export type EtapaVerificacao =
  | { tipo: "conflitos"; conflitos: ConflitoNovaAlocacao[] }
  | { tipo: "indisponivel"; itens: IndisponibilidadeNovaAlocacao[] }
  | { tipo: "incompleta"; itens: PapelIncompletoItem[] };

/** Situações confirmadas com justificativa no Salvar. */
export interface SituacoesConfirmadas {
  conflito: boolean;
  indisponibilidade: boolean;
}

export interface SalvarComVerificacoesParams {
  obra: EquipeObraTarget;
  /** Validação em tela das linhas; `false` interrompe antes de qualquer requisição. */
  validar: () => boolean;
  novas: NovaAlocacao[];
  /** Situação dos papéis obrigatórios (todos, adicionados ou não). */
  resumoObrigatorios: PapelIncompletoItem[];
  /** Listagem de profissionais do modal (indisponibilidades da HU 1761). */
  listagem: Profissional[];
  onErroVerificacao: (mensagem: string) => void;
  /**
   * O que acontece depois de todas as verificações: hoje o POST da equipe; o modo
   * simulação troca só esta ação. Recebe a justificativa única, quando houve, e quais
   * situações o usuário confirmou (define a mensagem de sucesso).
   */
  acaoFinal: (justificativa?: string, confirmadas?: SituacoesConfirmadas) => Promise<void> | void;
  /**
   * Modo Simulação: só o aviso de equipe incompleta. Conflito e indisponibilidade
   * seguem no RN011 depois de aplicar no cenário.
   */
  somenteIncompleta?: boolean;
}

/**
 * Fluxo do Salvar da equipe (HU 2059): valida em tela, busca as alocações do período e
 * percorre (1) conflitos de alocação, (2) profissional indisponível e (3) equipe
 * incompleta, nessa ordem; só então chama `acaoFinal`. "Voltar" em qualquer etapa
 * encerra o fluxo sem salvar.
 */
export function useSalvarComVerificacoes(params: SalvarComVerificacoesParams) {
  const [etapa, setEtapa] = useState<EtapaVerificacao | null>(null);
  const [verificando, setVerificando] = useState(false);
  const fila = useRef<EtapaVerificacao[]>([]);
  const justificativas = useRef<{ conflito?: string; indisponibilidade?: string }>({});
  /** Conflito e indisponibilidade na mesma fila: dividem o limite da justificativa. */
  const ambasJustificativas = useRef(false);
  const acaoFinal = useRef(params.acaoFinal);
  acaoFinal.current = params.acaoFinal;

  function avancar() {
    const proxima = fila.current.shift() ?? null;
    setEtapa(proxima);
    if (!proxima) {
      const { conflito, indisponibilidade } = justificativas.current;
      void acaoFinal.current(combinarJustificativas(justificativas.current), {
        conflito: Boolean(conflito),
        indisponibilidade: Boolean(indisponibilidade),
      });
    }
  }

  async function salvar() {
    if (verificando || etapa || !params.validar()) return;

    const { obra, novas } = params;
    const pendentes = papeisObrigatoriosPendentes(params.resumoObrigatorios);

    if (params.somenteIncompleta) {
      justificativas.current = {};
      fila.current =
        pendentes.length > 0
          ? [{ tipo: "incompleta" as const, itens: params.resumoObrigatorios }]
          : [];
      avancar();
      return;
    }

    setVerificando(true);
    let carga: CargaTrabalhoProfissional[] = [];
    try {
      // Sem profissional na equipe não há conflito nem indisponibilidade a consultar.
      if (novas.length > 0) {
        carga = await carregarAlocacoesDoPeriodo(
          { inicio: obra.inicio, fim: obra.fim },
          novas.map((nova) => nova.usuarioId),
        );
      }
    } catch {
      params.onErroVerificacao(VERIFICACAO_ERROR_MESSAGE);
      return;
    } finally {
      setVerificando(false);
    }

    const conflitos = detectarConflitos(novas, obra, carga);
    const indisponiveis = detectarIndisponibilidades(
      novas,
      unirIndisponibilidades(
        novas.map((nova) => nova.usuarioId),
        carga,
        params.listagem,
      ),
    );

    justificativas.current = {};
    ambasJustificativas.current = conflitos.length > 0 && indisponiveis.length > 0;
    fila.current = [
      ...(conflitos.length > 0 ? [{ tipo: "conflitos" as const, conflitos }] : []),
      ...(indisponiveis.length > 0
        ? [{ tipo: "indisponivel" as const, itens: indisponiveis }]
        : []),
      ...(pendentes.length > 0
        ? [{ tipo: "incompleta" as const, itens: params.resumoObrigatorios }]
        : []),
    ];
    avancar();
  }

  /**
   * Divergência apontada pelo servidor (os dados mudaram depois da verificação em tela):
   * reabre os modais correspondentes, com os dados do servidor; ao confirmar, `acaoFinal`
   * roda de novo com a justificativa informada.
   */
  function reabrirComDivergencias(
    conflitos: ConflitoNovaAlocacao[],
    indisponiveis: IndisponibilidadeNovaAlocacao[],
  ) {
    justificativas.current = {};
    ambasJustificativas.current = conflitos.length > 0 && indisponiveis.length > 0;
    fila.current = [
      ...(conflitos.length > 0 ? [{ tipo: "conflitos" as const, conflitos }] : []),
      ...(indisponiveis.length > 0
        ? [{ tipo: "indisponivel" as const, itens: indisponiveis }]
        : []),
    ];
    if (fila.current.length === 0) return;
    setEtapa(fila.current.shift() ?? null);
  }

  function confirmarConflitos(justificativa: string) {
    justificativas.current.conflito = justificativa;
    avancar();
  }

  function confirmarIndisponivel(justificativa: string) {
    justificativas.current.indisponibilidade = justificativa;
    avancar();
  }

  function cancelar() {
    fila.current = [];
    justificativas.current = {};
    setEtapa(null);
  }

  const maxJustificativa =
    etapa && etapa.tipo !== "incompleta"
      ? limiteJustificativa(
          etapa.tipo,
          ambasJustificativas.current,
          justificativas.current.conflito?.length,
        )
      : JUSTIFICATIVA_MAX_LENGTH;

  return {
    salvar,
    verificando,
    etapa,
    maxJustificativa,
    confirmarConflitos,
    confirmarIndisponivel,
    confirmarIncompleta: avancar,
    cancelar,
    reabrirComDivergencias,
  };
}
